/**
 * ShopCardCarousel — horizontal scrollable row of shop cards.
 * Used for "Recently Visited", "Recommended", and "Trending" sections.
 */

import { apiUrl } from '../../config'
import type { ShopCard } from '../../hooks/useHomePageData'
import './ShopCardCarousel.css'

interface Props {
  title: string
  icon: string
  shops: ShopCard[]
  emptyText?: string
}

/** Build poster URL with optional auth token for private shops */
function posterSrc(shop: ShopCard): string {
  const id = shop.realShopId || shop.shopId
  const base = apiUrl(`/api/shops/${id}/poster`)
  const token = localStorage.getItem('dukkan_token')
  return token ? `${base}?token=${encodeURIComponent(token)}` : base
}

/** Poster error fallback: hide broken img so CSS fallback emoji shows */
function handlePosterError(e: React.SyntheticEvent<HTMLImageElement>) {
  const img = e.target as HTMLImageElement
  img.style.display = 'none'
}

export default function ShopCardCarousel({ title, icon, shops, emptyText }: Props) {
  if (shops.length === 0 && !emptyText) return null

  const handleClick = (shopId: string) => {
    // Full page load required: the "/" route checks ?shopid at mount time
    window.location.href = `/?shopid=${shopId}`
  }

  return (
    <section className="shop-carousel-section">
      <h2 className="shop-carousel-title">
        <span className="shop-carousel-icon">{icon}</span>
        {title}
      </h2>
      {shops.length === 0 ? (
        <p className="shop-carousel-empty">{emptyText}</p>
      ) : (
        <div className="shop-carousel-scroll">
          {shops.map((shop) => (
            <button
              key={shop.shopId}
              className="shop-carousel-card"
              onClick={() => handleClick(shop.shopId)}
            >
              <div className="shop-carousel-poster">
                <img
                  src={posterSrc(shop)}
                  alt={shop.shopName}
                  loading="lazy"
                  onError={handlePosterError}
                />
                <div className="shop-carousel-poster-fallback">🏪</div>
              </div>
              <div className="shop-carousel-info">
                <span className="shop-carousel-name">{shop.shopName || 'Shop'}</span>
                {shop.category && (
                  <span className="shop-carousel-category">{shop.category}</span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  )
}
